import { useAppStoreLinks } from '@spaceship-fspl/graphql';
import { PostsOrPages } from '@tryghost/content-api';
import {
  getHeroPageLayoutStaticProps,
  getMetadataPropsFromPrismicSlice,
  HeaderProps,
  HeroPanel,
  mapNodeToHeaderProps,
  Metadata,
  MetadataProps,
  StickyHeader,
  withHeroPageLayout,
} from 'components';
import { LearnPanel } from 'components/learn-panel';
import { getPostsBySlugs } from 'data/ghost/api-cached';
import {
  getFirstHomeHomepage,
  getHeader,
  mapSlicesToProps,
  RichText,
} from 'data/prismic';
import {
  _ExternalLink,
  First_Home_HomepageBodyApps_Download,
  First_Home_HomepageBodyApps_DownloadPrimary,
  First_Home_HomepageBodyFaq,
  First_Home_HomepageBodyFaqFields,
  First_Home_HomepageBodyFaqPrimary,
  First_Home_HomepageBodyHero,
  First_Home_HomepageBodyHeroPrimary,
  First_Home_HomepageBodyHomebuyer_Programs,
  First_Home_HomepageBodyHomebuyer_ProgramsFields,
  First_Home_HomepageBodyHomebuyer_ProgramsPrimary,
  First_Home_HomepageBodyIntroduction,
  First_Home_HomepageBodyIntroductionPrimary,
  First_Home_HomepageBodyLearn,
  First_Home_HomepageBodyLearnFields,
  First_Home_HomepageBodyLearnPrimary,
  First_Home_HomepageBodyLower_Deposit,
  First_Home_HomepageBodyLower_DepositFields,
  First_Home_HomepageBodyLower_DepositPrimary,
  First_Home_HomepageBodySign_Up,
  First_Home_HomepageBodySign_UpPrimary,
  First_Home_HomepageBodySimple_Sign_Up,
  First_Home_HomepageBodySimple_Sign_UpPrimary,
  First_Home_HomepageBodyStates,
  First_Home_HomepageBodyStatesFields,
  First_Home_HomepageBodyStatesPrimary,
} from 'data/prismic/types';
import faqDesktopImage from 'images/first-home_faq-desktop.svg';
import faqMobileImage from 'images/first-home_faq-mobile.svg';
import { Link } from 'prismic-reactjs';
import React, { HTMLAttributeAnchorTarget } from 'react';

import { AppsDownloadPanel } from './components/apps-download-panel';
import { FaqPanel } from './components/faq-panel';
import { HomebuyerProgramsPanel } from './components/homebuyer-programs-panel';
import { IntroductionPanel } from './components/introduction-panel';
import { LowerDepositPanel } from './components/lower-deposit-panel';
import { SignUpPanel } from './components/signup-panel';
import { SimpleSignupPanel } from './components/simple-signup-panel';
import { StatesPanel } from './components/states-panel';

interface FirstHomePageProps {
  headerProps?: HeaderProps;
  prismicData: {
    First_home_homepageBodyMetadata?: MetadataProps | null;
    First_home_homepageBodyHero?: First_Home_HomepageBodyHeroPrimary | null;
    First_home_homepageBodyIntroduction?: First_Home_HomepageBodyIntroductionPrimary | null;
    First_home_homepageBodyHomebuyer_programs?:
      | (First_Home_HomepageBodyHomebuyer_ProgramsPrimary & {
          programs?: First_Home_HomepageBodyHomebuyer_ProgramsFields[] | null;
        })
      | null;
    First_home_homepageBodyStates?:
      | (First_Home_HomepageBodyStatesPrimary & {
          states?: First_Home_HomepageBodyStatesFields[] | null;
        })
      | null;
    First_home_homepageBodyFaq?:
      | (First_Home_HomepageBodyFaqPrimary & {
          faqs?: First_Home_HomepageBodyFaqFields[] | null;
        })
      | null;
    First_home_homepageBodyApps_download?: First_Home_HomepageBodyApps_DownloadPrimary | null;
    First_home_homepageBodySimple_sign_up?: First_Home_HomepageBodySimple_Sign_UpPrimary | null;
    First_home_homepageBodyLower_deposit?:
      | (First_Home_HomepageBodyLower_DepositPrimary & {
          states?: First_Home_HomepageBodyLower_DepositFields[] | null;
        })
      | null;
    First_home_homepageBodySign_up?: First_Home_HomepageBodySign_UpPrimary | null;
    First_home_homepageBodyLearn?:
      | (First_Home_HomepageBodyLearnPrimary & {
          posts?: First_Home_HomepageBodyLearnFields[] | null;
        })
      | null;
  };
  ghostData: {
    First_home_homepageBodyLearn: PostsOrPages;
  };
}

const FirstHomePage: React.FC<FirstHomePageProps> = ({
  headerProps,
  prismicData: {
    First_home_homepageBodyMetadata: metadata,
    First_home_homepageBodyHero: hero,
    First_home_homepageBodyIntroduction: introduction,
    First_home_homepageBodyHomebuyer_programs: homebuyerPrograms,
    First_home_homepageBodyStates: states,
    First_home_homepageBodyFaq: faq,
    First_home_homepageBodyApps_download: appsDownload,
    First_home_homepageBodySimple_sign_up: simpleSignup,
    First_home_homepageBodyLower_deposit: lowerDeposit,
    First_home_homepageBodySign_up: signup,
    First_home_homepageBodyLearn: learn,
  },
  ghostData: { First_home_homepageBodyLearn: learnPosts },
}) => {
  const appStoreLinksResp = useAppStoreLinks();

  return (
    <>
      {metadata && <Metadata {...metadata} />}

      {hero && (
        <HeroPanel
          variant="first-home"
          product={hero?.product ?? ''}
          title={hero?.title ?? ''}
          tagline={<RichText render={hero?.richtagline} variant="light" />}
          footnote={<RichText render={hero?.richfootnote} variant="light" />}
          headerComponent={(props) => (
            <StickyHeader {...props} {...headerProps} />
          )}
        />
      )}

      {introduction && (
        <IntroductionPanel
          title={introduction.title}
          tagline={<RichText render={introduction.richtagline} />}
        />
      )}

      {homebuyerPrograms && (
        <HomebuyerProgramsPanel
          title={homebuyerPrograms.title}
          tagline={<RichText render={homebuyerPrograms.richtagline} />}
          programs={homebuyerPrograms.programs?.map((program) => ({
            type: program.type,
            title: program.title,
            description: <RichText render={program.richdescription} />,
            learnMoreText: program.learnmorebuttontext,
            learnMoreLink: program.learnmorebuttonlink,
          }))}
        />
      )}

      {states && (
        <StatesPanel
          title={states.title}
          tagline={<RichText render={states.richtagline} />}
          states={states.states?.map((stateData) => ({
            state: stateData.state,
            title: stateData.title,
            description: <RichText render={stateData.richdescription} />,
          }))}
        />
      )}

      {faq && (
        <FaqPanel
          id={faq.id}
          title={faq.title}
          tagline={<RichText render={faq.richtagline} />}
          desktopImage={faqDesktopImage}
          mobileImage={faqMobileImage}
          faqs={faq.faqs?.map((faqData) => ({
            title: faqData.title,
            description: <RichText render={faqData.richdescription} />,
          }))}
        />
      )}

      {appsDownload && (
        <AppsDownloadPanel
          title={appsDownload.title}
          appStoreUrl={appStoreLinksResp.data?.appleAppStoreDeeplink?.url}
          playStoreUrl={appStoreLinksResp.data?.googlePlayStoreDeeplink?.url}
        />
      )}

      {simpleSignup && (
        <SimpleSignupPanel
          title={simpleSignup.title}
          signupText={simpleSignup.signupbuttontext}
          signupUrl={Link.url(simpleSignup.signupbuttonurl)}
          signupUrlTarget={
            simpleSignup.signupbuttonurl
              ? ((simpleSignup.signupbuttonurl as _ExternalLink)
                  .target as HTMLAttributeAnchorTarget)
              : undefined
          }
        />
      )}

      {lowerDeposit && (
        <LowerDepositPanel
          id={lowerDeposit.id}
          title={lowerDeposit.title}
          tagline={<RichText render={lowerDeposit.richtagline} />}
          faqTitle={lowerDeposit.faqtitle}
          faqDescription={<RichText render={lowerDeposit.richfaqdescription} />}
          statesTitle={lowerDeposit.statestitle}
          states={lowerDeposit.states}
        />
      )}

      {signup && (
        <SignUpPanel
          variant="homepage"
          title={signup.title}
          tagline={<RichText render={signup.richtagline} />}
          disclaimer={<RichText render={signup.richdisclaimer} />}
          signupText={signup.signupbuttontext}
          signupUrl={Link.url(signup.signupbuttonurl)}
          signupUrlTarget={
            signup.signupbuttonurl
              ? ((signup.signupbuttonurl as _ExternalLink)
                  .target as HTMLAttributeAnchorTarget)
              : undefined
          }
        />
      )}

      {learn && (
        <LearnPanel
          title={learn.title ?? ''}
          columns={learn.posts?.length}
          posts={learnPosts}
          hasMore={false}
        />
      )}
    </>
  );
};

export const getStaticProps = getHeroPageLayoutStaticProps<FirstHomePageProps>(
  async () => {
    const header = await getHeader();
    const document = await getFirstHomeHomepage();

    const prismicData = mapSlicesToProps(document, {
      First_home_homepageBodyMetadata: (slice) =>
        getMetadataPropsFromPrismicSlice(slice),
      First_home_homepageBodyHero: (slice: First_Home_HomepageBodyHero) => ({
        ...slice.primary,
      }),
      First_home_homepageBodyIntroduction: (
        slice: First_Home_HomepageBodyIntroduction,
      ) => ({
        ...slice.primary,
      }),
      First_home_homepageBodyHomebuyer_programs: (
        slice: First_Home_HomepageBodyHomebuyer_Programs,
      ) => ({
        ...slice.primary,
        programs: slice.fields,
      }),
      First_home_homepageBodyStates: (
        slice: First_Home_HomepageBodyStates,
      ) => ({
        ...slice.primary,
        states: slice.fields,
      }),
      First_home_homepageBodyFaq: (slice: First_Home_HomepageBodyFaq) => ({
        ...slice.primary,
        faqs: slice.fields,
      }),
      First_home_homepageBodyApps_download: (
        slice: First_Home_HomepageBodyApps_Download,
      ) => ({
        ...slice.primary,
      }),
      First_home_homepageBodySimple_sign_up: (
        slice: First_Home_HomepageBodySimple_Sign_Up,
      ) => ({
        ...slice.primary,
      }),
      First_home_homepageBodyLower_deposit: (
        slice: First_Home_HomepageBodyLower_Deposit,
      ) => ({
        ...slice.primary,
        states: slice.fields,
      }),
      First_home_homepageBodySign_up: (
        slice: First_Home_HomepageBodySign_Up,
      ) => ({
        ...slice.primary,
      }),
      First_home_homepageBodyLearn: (slice: First_Home_HomepageBodyLearn) => ({
        ...slice.primary,
        posts: slice.fields,
      }),
    });

    const learnPosts = await getPostsBySlugs(
      prismicData.First_home_homepageBodyLearn?.posts?.map(
        (post) => post.postslug ?? '',
      ) ?? [],
    );

    return {
      props: {
        headerProps: mapNodeToHeaderProps(header),
        prismicData,
        ghostData: {
          First_home_homepageBodyLearn: learnPosts,
        },
      },
    };
  },
);

export default withHeroPageLayout({ product: 'super', showSignUpPanel: false })(
  FirstHomePage,
);
